<template>
  <div class="sp-api-page">
    <section class="hero">
      <div class="container">
        <p class="eyebrow">Amazon Selling Partner API</p>
        <h1>{{ texts.title }}</h1>
        <p class="lead">{{ texts.lead }}</p>
      </div>
    </section>

    <section class="content">
      <div class="container card">
        <h2>{{ texts.audienceTitle }}</h2>
        <p>{{ texts.audienceText1 }}</p>
        <p>{{ texts.audienceText2 }}</p>

        <h2>{{ texts.featureTitle }}</h2>
        <ul>
          <li v-for="item in texts.featureItems" :key="item">{{ item }}</li>
        </ul>

        <h2>{{ texts.boundaryTitle }}</h2>
        <ul>
          <li v-for="item in texts.boundaryItems" :key="item">{{ item }}</li>
        </ul>

        <h2>{{ texts.permissionTitle }}</h2>
        <p>{{ texts.permissionText1 }}</p>
        <p>{{ texts.permissionText2 }}</p>

        <h2>{{ texts.securityTitle }}</h2>
        <ul>
          <li v-for="item in texts.securityItems" :key="item">{{ item }}</li>
        </ul>

        <div class="notice">
          <strong>{{ texts.noticeLabel }}</strong>
          {{ texts.noticeText }}
        </div>

        <div class="actions">
          <router-link :to="{ name: 'BrandAnalyticsDetail' }" class="action-link">
            {{ texts.brandLink }}
          </router-link>
          <router-link :to="{ name: 'privacyDetail' }" class="action-link">
            {{ texts.privacyLink }}
          </router-link>
          <a class="action-link primary" :href="contactHref">
            {{ texts.contactLink }}
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { localeState } from "@/utils/locale";

export default {
  name: "SpApiCompliance",
  data() {
    return {
      copy: {
        zh: {
          title: "SP-API 服务说明与合规声明",
          lead:
            "本网站用于公开说明深圳市万俐电子商务有限公司为亚马逊卖家及品牌所有者提供的 SaaS 运营管理服务，以及我们对 Amazon SP-API 数据使用、权限申请和安全合规的承诺。",
          audienceTitle: "1. 服务对象与授权方式",
          audienceText1:
            "我们的系统面向亚马逊卖家、品牌所有者及其授权团队成员提供服务。只有在客户完成明确授权后，我们才会通过 Amazon SP-API 访问其店铺或品牌相关数据。",
          audienceText2:
            "所有数据访问均以客户授权为前提，客户可根据业务需要授予、调整或撤销相关权限。",
          featureTitle: "2. 应用功能说明",
          featureItems: [
            "订单与履约管理：用于同步订单、发货、物流和售后处理所需的信息。",
            "库存与供应链协同：用于库存监控、补货建议、采购和仓储流程管理。",
            "财务与经营报表：用于账单核对、利润统计、经营看板和内部决策分析。",
            "广告与运营分析：用于广告投放监控、效果分析和运营效率提升。",
            "品牌分析相关功能：仅在客户具备相应资格并明确授权后，用于展示其自有品牌的经营分析结果。",
          ],
          boundaryTitle: "3. 数据使用边界",
          boundaryItems: [
            "我们不会出售、转让、出租、许可或公开披露通过 Amazon SP-API 获取的数据。",
            "我们不会将多个卖家或多个品牌的 Amazon 数据汇总后对外提供、展示或售卖。",
            "每个客户的数据在系统内按账户隔离，仅用于该客户自身的业务管理和分析。",
            "如涉及买家个人信息，仅限于订单履约、售后服务等必要场景使用，并遵循最小化原则。",
          ],
          permissionTitle: "4. 权限申请原则",
          permissionText1:
            "我们仅申请为上述产品功能提供服务所必需的 SP-API 权限，实际申请范围以 Amazon 开发者资料中提交的角色与权限说明为准。",
          permissionText2:
            "如申请品牌分析相关角色，该能力仅服务于已授权卖家或品牌所有者的内部经营分析，不面向公众提供聚合数据产品。",
          securityTitle: "5. 数据安全与隐私保护",
          securityItems: [
            "对敏感数据传输与存储采取加密保护措施，并实施最小权限访问控制。",
            "仅授权员工可访问履行岗位职责所需的数据，并保留访问日志用于审计。",
            "客户可依据协议及隐私政策申请查看、调整或撤销相关授权。",
          ],
          noticeLabel: "合规声明：",
          noticeText:
            "深圳市万俐电子商务有限公司提供的是面向授权客户的管理软件服务，而非亚马逊数据转售、公开数据分发或跨卖家数据交易服务。",
          brandLink: "查看品牌分析功能",
          privacyLink: "查看隐私政策",
          contactLink: "联系我们",
          contactSubject: "SP-API 合规咨询",
        },
        en: {
          title: "SP-API Service Overview and Compliance Statement",
          lead:
            "This page publicly explains the SaaS operational management services provided by Hunan Panxian Intelligent Technology Co., Ltd. for Amazon sellers and brand owners, as well as our commitments regarding Amazon SP-API data usage, permission requests, and security compliance.",
          audienceTitle: "1. Service Audience and Authorization Method",
          audienceText1:
            "Our system serves Amazon sellers, brand owners, and their authorized team members. We access store or brand-related data through Amazon SP-API only after the customer has provided explicit authorization.",
          audienceText2:
            "All data access is based on customer authorization, and customers may grant, adjust, or revoke relevant permissions according to business needs.",
          featureTitle: "2. Application Functions",
          featureItems: [
            "Order and fulfillment management: synchronize information required for orders, shipping, logistics, and after-sales processing.",
            "Inventory and supply chain collaboration: support inventory monitoring, replenishment recommendations, purchasing, and warehouse workflow management.",
            "Financial and business reporting: support bill reconciliation, profit analysis, business dashboards, and internal decision-making.",
            "Advertising and operations analysis: support campaign monitoring, performance analysis, and operational efficiency improvement.",
            "Brand analytics features: available only when the customer has the required eligibility and explicit authorization to view analytics for its own brand.",
          ],
          boundaryTitle: "3. Data Usage Boundaries",
          boundaryItems: [
            "We do not sell, transfer, rent, license, or publicly disclose data obtained through Amazon SP-API.",
            "We do not aggregate Amazon data from multiple sellers or brands for external display, delivery, or sale.",
            "Each customer's data is isolated by account in the system and is used only for that customer's own business management and analysis.",
            "If buyer personal information is involved, it is used only for necessary scenarios such as order fulfillment and after-sales service, following the principle of minimum necessary use.",
          ],
          permissionTitle: "4. Permission Request Principles",
          permissionText1:
            "We request only the SP-API permissions necessary to provide the product functions described above. The actual scope is subject to the roles and permissions submitted in our Amazon developer profile.",
          permissionText2:
            "If Brand Analytics-related roles are requested, that capability serves only the internal business analysis of authorized sellers or brand owners and is not offered as a public aggregated data product.",
          securityTitle: "5. Data Security and Privacy Protection",
          securityItems: [
            "We apply encryption measures for sensitive data in transit and at rest, together with least-privilege access controls.",
            "Only authorized employees may access data required for their job responsibilities, and access logs are retained for auditing.",
            "Customers may request to review, adjust, or revoke relevant authorizations in accordance with applicable agreements and privacy policies.",
          ],
          noticeLabel: "Compliance Statement:",
          noticeText:
            "Hunan Panxian Intelligent Technology Co., Ltd. provides management software services for authorized customers, not Amazon data resale, public data distribution, or cross-seller data trading services.",
          brandLink: "View Brand Analytics Features",
          privacyLink: "View Privacy Policy",
          contactLink: "Contact Us",
          contactSubject: "SP-API compliance inquiry",
        },
      },
    };
  },
  computed: {
    locale() {
      return localeState.locale;
    },
    texts() {
      return this.copy[this.locale] || this.copy.zh;
    },
    contactHref() {
      return `mailto:Lin@whanley.com?subject=${encodeURIComponent(
        this.texts.contactSubject
      )}`;
    },
  },
};
</script>

<style scoped>
.sp-api-page {
  background: #f5f8ff;
  min-height: 100%;
  padding-bottom: 4rem;
}

.hero {
  background: linear-gradient(135deg, #0f3d91, #2554fe 65%, #6ea2ff);
  color: #fff;
  padding: 4.5rem 1.25rem 3.5rem;
}

.container {
  margin: 0 auto;
  max-width: 80rem;
}

.eyebrow {
  font-size: 0.875rem;
  letter-spacing: 0.12em;
  margin-bottom: 0.75rem;
  opacity: 0.82;
  text-transform: uppercase;
}

h1 {
  font-size: 2.25rem;
  line-height: 1.25;
  margin: 0;
}

.lead {
  font-size: 1.05rem;
  line-height: 1.9;
  margin-top: 1rem;
  max-width: 56rem;
}

.content {
  padding: 0 1.25rem;
}

.card {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 18px 48px rgba(37, 84, 254, 0.12);
  margin-top: -2rem;
  padding: 2rem 1.5rem;
}

h2 {
  color: #173169;
  font-size: 1.25rem;
  margin: 1.5rem 0 0.75rem;
  text-align: left;
}

p,
li {
  color: #344054;
  font-size: 0.98rem;
  line-height: 1.9;
  text-align: left;
}

ul {
  margin: 0;
  padding-left: 1.25rem;
}

.notice {
  background: #edf4ff;
  border: 1px solid #cfe0ff;
  border-radius: 0.875rem;
  color: #173169;
  line-height: 1.8;
  margin-top: 2rem;
  padding: 1rem 1.125rem;
  text-align: left;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.875rem;
  margin-top: 1.5rem;
}

.action-link {
  border: 1px solid #2554fe;
  border-radius: 999px;
  color: #2554fe;
  padding: 0.75rem 1.25rem;
  text-decoration: none;
}

.action-link.primary {
  background: #2554fe;
  color: #fff;
}

@media (min-width: 1024px) {
  .hero {
    padding: 5.5rem 2rem 4.25rem;
  }

  .card {
    margin-top: -2.75rem;
    padding: 2.75rem;
  }
}
</style>
